import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Announcement {
  id: string;
  title: string;
  message: string;
  publish_to: string;
  expire_date: string | null;
  priority: 'normal' | 'urgent';
  enable_interactions: boolean;
  send_in_app: boolean;
  send_email: boolean;
  is_active: boolean;
  created_at: string;
  created_by: string;
}

export const useAnnouncements = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['announcements'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Announcement[];
    },
    enabled: !!user,
  });
};

export const useUnreadAnnouncementsCount = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['unread-announcements-count'],
    queryFn: async () => {
      if (!user) return 0;

      // Get current employee ID using database function
      const { data: employeeId, error: employeeError } = await supabase
        .rpc('get_current_employee_id');

      if (employeeError || !employeeId) return 0;

      // Get all active announcements
      const { data: announcements } = await supabase
        .from('announcements')
        .select('id')
        .eq('is_active', true)
        .or(`expire_date.is.null,expire_date.gte.${new Date().toISOString().split('T')[0]}`);

      if (!announcements) return 0;

      // Get read announcements by the current user
      const { data: readAnnouncements } = await supabase
        .from('announcement_reads')
        .select('announcement_id')
        .eq('employee_id', employeeId);

      const readIds = readAnnouncements?.map(r => r.announcement_id) || [];
      const unreadCount = announcements.filter(a => !readIds.includes(a.id)).length;

      return unreadCount;
    },
    enabled: !!user,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

export const useMarkAnnouncementAsRead = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (announcementId: string) => {
      if (!user) throw new Error('User not authenticated');

      // Get current employee ID using database function
      const { data: employeeId, error: employeeError } = await supabase
        .rpc('get_current_employee_id');

      if (employeeError || !employeeId) {
        throw new Error('Employee not found or not authenticated');
      }

      const { error } = await supabase
        .from('announcement_reads')
        .upsert({
          announcement_id: announcementId,
          employee_id: employeeId
        }, {
          onConflict: 'announcement_id,employee_id' 
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unread-announcements-count'] });
    },
  });
};